import type { Permission, SessionUser } from '@edms/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, type ReactNode } from 'react';
import { api, ApiError } from './api';
import { disablePush } from './push';

interface AuthState {
  user: SessionUser | null;
  loading: boolean;
  can: (permission: Permission) => boolean;
  setUser: (user: SessionUser | null) => void;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const me = useQuery({
    queryKey: ['me'],
    queryFn: async () => {
      try {
        return await api.get<SessionUser>('/auth/me');
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) return null;
        throw e;
      }
    },
    staleTime: 60_000,
    retry: false,
  });

  const setUser = useCallback((user: SessionUser | null) => qc.setQueryData(['me'], user), [qc]);
  /**
   * Вышли или сессия кончилась: сначала «пользователя нет» — экран, подписанный на ['me'],
   * сразу уходит на вход; затем удаляются чужие данные. Если сначала очистить весь кэш,
   * подписка остаётся на удалённой записи и экран не обновляется до перезагрузки.
   */
  const signedOut = useCallback(() => {
    qc.setQueryData(['me'], null);
    qc.removeQueries({ predicate: (q) => q.queryKey[0] !== 'me' });
  }, [qc]);

  useEffect(() => {
    // Сессия истекла или пользователя отключили — возвращаем на вход.
    const onUnauthorized = () => signedOut();
    window.addEventListener('edms:unauthorized', onUnauthorized);
    return () => window.removeEventListener('edms:unauthorized', onUnauthorized);
  }, [signedOut]);

  const user = me.data ?? null;
  const value: AuthState = {
    user,
    loading: me.isPending,
    can: (p) => Boolean(user?.permissions.includes(p)),
    setUser,
    logout: async () => {
      // Это устройство больше не получает уведомления вышедшего сотрудника.
      await disablePush().catch(() => {});
      await api.post('/auth/logout').catch(() => {});
      signedOut();
    },
  };
  return <AuthContext value={value}>{children}</AuthContext>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth вне AuthProvider');
  return ctx;
}
